/**
 * roomCrypto.js — crittografia delle Room (chat di gruppo effimere).
 *
 * Modello: FAN-OUT PAIRWISE. Una Room NON ha una chiave di gruppo condivisa.
 * Ogni messaggio è cifrato N volte, una per ciascun membro, con la sua
 * pubKeyBox (X25519 box, gli stessi primitivi delle chat 1:1). Il server
 * archivia `encryptedPayloads:[{handle, ciphertext, nonce}]`, ogni client
 * decifra SOLO il payload destinato al proprio handle.
 *
 * Vantaggi (vedi Nullum-rooms-spec.md §4):
 *  - zero superficie crittografica nuova (riusa crypto.js / keyDerivation.js)
 *  - stateless: niente group-key, niente ratchet, niente rotazione su join/leave
 *  - il "burn" è una semplice DELETE lato server
 *
 * Identità in room = USA-E-GETTA, derivata per-room da keyDerivation
 * (purpose='room', context=roomId): l'handle non rivela il sigil 1:1 agli altri
 * membri. CONFINE ONESTO: protegge dagli altri MEMBRI, non dal server (la mappa
 * tecnica handle→account esiste lato server, come per i sigil derivati).
 */

import { deriveRoomContext } from './keyDerivation.js';
import {
  initSodium, deriveUid, boxEncrypt, boxDecrypt, toBase64Url, fromBase64Url,
} from './crypto.js';

/**
 * Deriva l'identità usa-e-getta del chiamante per una specifica Room.
 * Deterministica: stesso (masterSeed, roomId) → stesso handle e stesse chiavi.
 *
 * @param {Uint8Array} masterSeed - seed master BIP-39 (64 byte)
 * @param {string}     roomId     - id della room (1..64 char [A-Za-z0-9_-])
 * @returns {Promise<{handle, pubKeyBox, pubKeyBoxRaw, secKeyBox, pubKeySign}>}
 */
export async function deriveRoomIdentity(masterSeed, roomId) {
  const keys = await deriveRoomContext(masterSeed, roomId);
  // Handle opaco e stabile per (seed, room): stessa funzione uid del resto dell'app.
  const handle = await deriveUid(keys.sign.publicKey);
  return {
    handle,                                       // 22 char, mostrato/registrato in room
    pubKeyBox: await toBase64Url(keys.box.publicKey), // per membership/trasporto
    pubKeyBoxRaw: keys.box.publicKey,             // 32 byte, per cifrare/decifrare
    secKeyBox: keys.box.privateKey,               // resta SOLO sul device
    pubKeySign: await toBase64Url(keys.sign.publicKey),
  };
}

/**
 * Cifra un messaggio per TUTTI i membri della room (fan-out).
 * Includi te stesso tra i membri per poter rileggere ciò che hai inviato.
 *
 * @param {string|Uint8Array} plaintext
 * @param {Uint8Array} senderSecKeyBox           - secKeyBox del mittente (room)
 * @param {Array<{handle:string, pubKeyBox:string}>} members - pubKeyBox in base64url
 * @returns {Promise<Array<{handle, ciphertext, nonce}>>} encryptedPayloads (base64url)
 */
export async function encryptForRoom(plaintext, senderSecKeyBox, members) {
  await initSodium();
  if (!Array.isArray(members) || members.length === 0) {
    throw new Error('roomCrypto: members vuoto');
  }
  const payloads = [];
  for (const m of members) {
    const recipPub = await fromBase64Url(m.pubKeyBox);
    const { ciphertext, nonce } = await boxEncrypt(plaintext, recipPub, senderSecKeyBox);
    payloads.push({
      handle: m.handle,
      ciphertext: await toBase64Url(ciphertext),
      nonce: await toBase64Url(nonce),
    });
  }
  return payloads;
}

/**
 * Decifra il payload destinato al mio handle. Ritorna null se non sono tra i
 * destinatari (es. messaggio inviato PRIMA che entrassi: niente storico al join).
 *
 * @param {Array<{handle, ciphertext, nonce}>} payloads
 * @param {string} myHandle
 * @param {string|Uint8Array} senderPubKeyBox - pubKeyBox del mittente (room), b64 o raw
 * @param {Uint8Array} mySecKeyBox
 * @returns {Promise<string|null>}
 */
export async function decryptRoomMessage(payloads, myHandle, senderPubKeyBox, mySecKeyBox) {
  if (!Array.isArray(payloads)) return null;
  const mine = payloads.find((p) => p && p.handle === myHandle);
  if (!mine) return null;
  const senderPub = typeof senderPubKeyBox === 'string'
    ? await fromBase64Url(senderPubKeyBox)
    : senderPubKeyBox;
  const ct = await fromBase64Url(mine.ciphertext);
  const nonce = await fromBase64Url(mine.nonce);
  return boxDecrypt(ct, nonce, senderPub, mySecKeyBox);
}
